import { validateObject, validateField } from "util/validation";
import { notBlank } from "../../../util/ext";
import { curry, mergeRight } from "ramda";

export const requiredFields = {
  ARTICLE: ["description", "language", "licenseType", "title"],
  AUDIO: ["audioType", "description", "language", "licenseType", "title"],
  AUDIOBOOK: [
    "categories",
    "contributors",
    "description",
    "language",
    "licenseType",
    "title",
  ],
  BOOK: [
    "categories",
    "contributors",
    "description",
    "language",
    "licenseTypeCode",
    "title",
  ],
  IMAGE: ["description", "language", "licenseType", "mediumFormat", "title"],
  OTHER_DOCUMENT: [
    "description",
    "language",
    "licenseType",
    "mediumFormat",
    "title",
  ],
  VIDEO: ["description", "language", "licenseType", "title"],
};

const submissionBaseConstraints = {
  title: {
    presence: {
      allowEmpty: false,
      message: "^Title must not be blank",
    },
  },
  description: {
    presence: {
      allowEmpty: false,
      message: "^Description must not be blank",
    },
  },
  language: {
    presence: {
      allowEmpty: false,
      message: "^A language must be selected",
    },
  },
  licenseType: {
    presence: {
      allowEmpty: false,
      message: "^A license type must be selected",
    },
  },
};

const submissionTypeConstraints = {
  AUDIO: {
    audioType: {
      presence: {
        allowEmpty: false,
        message: "^An audio type must be selected",
      },
    },
  },
  AUDIOBOOK: {
    categories: {
      presence: {
        allowEmpty: false,
        message: "^At least one category must be selected",
      },
    },
    contributors: {
      presence: {
        allowEmpty: false,
        message: "^At least one contributor must be added",
      },
    },
  },
  BOOK: {
    categories: {
      presence: {
        allowEmpty: false,
        message: "^At least one category must be selected",
      },
    },
    contributors: {
      presence: {
        allowEmpty: false,
        message: "^At least one contributor must be added",
      },
    },
  },
  IMAGE: {
    mediumFormat: {
      presence: { allowEmpty: false, message: "^A format must be selected" },
    },
  },
  OTHER_DOCUMENT: {
    mediumFormat: {
      presence: { allowEmpty: false, message: "^A format must be selected" },
    },
  },
};

const contributorConstraints = type => {
  let constraints = {
    role: {
      presence: {
        allowEmpty: false,
        message: "^You must select a role",
      },
    },
    website: {
      presence: false,
      format: {
        pattern: /^[^ "]+$/,
        message: "^Invalid website",
      },
    },
    twitterHandle: {
      presence: false,
      format: {
        pattern: /^@[A-Za-z0-9_]+$/,
        message: "^Invalid X handle",
      },
    },
  };

  if (type === "PERSON") {
    constraints = mergeRight(
      {
        firstName: {
          presence: {
            allowEmpty: false,
            message: "^First name is required",
          },
        },
        lastName: {
          presence: {
            allowEmpty: false,
            message: "^Last name is required",
          },
        },
      },
      constraints,
    );
  } else {
    constraints = mergeRight(
      {
        fullName: {
          presence: {
            allowEmpty: false,
            message: "^Corporation name is required",
          },
        },
      },
      constraints,
    );
  }

  return constraints;
};

export const getSubmissionConstraints = type =>
  mergeRight(submissionBaseConstraints, submissionTypeConstraints[type]);

export const validateSubmissionField = (type, field, value, errors = {}) =>
  validateField(getSubmissionConstraints(type), field, value, errors);

export const validateSubmission = curry((type, fields) =>
  validateObject(getSubmissionConstraints(type), fields),
);

export const validateContributorField = (type, field, value, errors = {}) =>
  validateField(contributorConstraints(type), field, value, errors);

export const validateContributor = curry((type, fields) =>
  validateObject(contributorConstraints(type), fields),
);

export const hasError = curry((errors, field) => notBlank(errors[field]));

export const firstError = curry((errors, field) =>
  errors[field] ? errors[field][0] : "",
);
